import { zodResolver } from "@hookform/resolvers/zod";
import { TFunction } from "i18next";
import React, { useEffect, useMemo, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { z } from "zod";
import useCheckNumber, {
  SignupRequest as SignRequest,
} from "../../../api/auth/register/useCheckNumber";
import {
  GeneralButton,
  GeneralInput,
  Loader,
  Modal,
  PhoneNumberInput,
} from "../../../components";
import i18n from "../../../i18n";
import AuthLayout from "../../../Layout/AuthLayout";
import { setUserDetails } from "../../../redux/reducers/registrationSlice";
import { RootState } from "../../../redux/store";
import { getLanguageSpecificProps } from "../../../utils/getLanguageSpecificProps";

interface SignFormInputs {
  phone: string;
  fullName: string;
}

const SignUp: React.FC = () => {
  const dispatch = useDispatch();
  const { mutate, isPending } = useCheckNumber();
  const validate = useSelector((state: RootState) => state.phValidation);
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Create a function to generate the validation schema
  const createSignSchema = (t: TFunction) => {
    return z.object({
      fullName: z
        .string()
        .min(
          4,
          t("name_must_be") + " 4 " + t("characters") + " " + t("at_least"),
        ),
      phone: z
        .string()
        .min(9, t("phone_number_required"))
        .superRefine((value, ctx) => {
          if (!validate) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t("validation_configuration_error"),
            });
            return;
          }

          // Remove any non-numeric characters
          const phoneNumber = value.replace(/[^0-9]/g, "");
          const countryStart = validate.Start.toString();
          const countryLength = validate.Length;

          // Define the valid starting sequences
          const startWith0 = `0${countryStart}`;
          const startWithCountryStart = `${countryStart}`;

          if (phoneNumber.startsWith(startWith0)) {
            if (phoneNumber.length !== countryLength + 1) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t("phone_number_must_be_and_start_with", {
                  length: countryLength + 1,
                  start: startWith0,
                }),
              });
            }
          } else if (phoneNumber.startsWith(startWithCountryStart)) {
            if (phoneNumber.length !== countryLength) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t("phone_number_must_be_and_start_with", {
                  length: countryLength,
                  start: startWithCountryStart,
                }),
              });
            }
          } else {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t("phone_number_must_start_with", {
                start1: startWithCountryStart,
                start2: startWith0,
              }),
            });
          }
        }),
    });
  };

  // Use useMemo to create the validation schema
  const validationSchema = useMemo(() => createSignSchema(t), [t, validate]);

  // Initialize the form methods using react-hook-form and zodResolver
  const methods = useForm<SignFormInputs>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      phone: "",
      fullName: "",
    },
  });

  const {
    handleSubmit,
    setError,
    trigger,
    formState: { isSubmitted },
  } = methods;

  // Re-trigger validation when language changes, but only if the form has been submitted
  useEffect(() => {
    if (isSubmitted) {
      trigger();
    }
  }, [t, trigger, isSubmitted]);

  // Handle form submission
  const onSubmit: SubmitHandler<SignFormInputs> = (data) => {
    setIsModalOpen(true);
  };

  // Confirm the signup process
  const handleConfirm = () => {
    const data = methods.getValues();

    // Remove leading '0' if present
    let phoneNumber = data.phone;
    if (phoneNumber.startsWith("0")) {
      phoneNumber = phoneNumber.substring(1);
    }

    // Prepend the country code
    const requestData: SignRequest = {
      CountryId: validate.CountryId,
      FullName: data.fullName,
      PhoneNumber: phoneNumber,
    };

    dispatch(
      setUserDetails({
        PhoneNumber: phoneNumber,
        CountryId: validate.CountryId,
        FullName: data.fullName,
      }),
    );

    mutate(requestData);
    setIsModalOpen(false);
  };

  // Helper function to format phone number for display
  const formatPhoneNumberForDisplay = (phone: string): string => {
    if (!validate) return phone;
    const countryStart = validate.Start.toString();
    const startWith0 = `0${countryStart}`;

    if (phone.startsWith(startWith0)) {
      return phone.slice(1); // Remove the leading '0'
    }
    return phone;
  };

  // Get the formatted phone number
  const formattedPhoneNumber = formatPhoneNumberForDisplay(
    methods.getValues().phone,
  );
  const currentLang = i18n.language?.slice(0, 2);
  // Full phone number with country code
  const displayPhoneNumber = `${validate?.Code || ""} ${formattedPhoneNumber}`;

  return (
    <FormProvider {...methods}>
      <AuthLayout title={t("welcome")} description="">
        <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-16 flex flex-col gap-5">
            <GeneralInput
              type="text"
              name="fullName"
              charactersOnly
              label={t("full_name")}
              placeholder={t("full_name")}
              {...getLanguageSpecificProps(currentLang)}
              required={true}
            />
            <PhoneNumberInput />
          </div>
          <GeneralButton
            disabled={isPending}
            title={t("create_account")}
            color="blue"
            type="submit"
            block
          />
        </form>
        {isPending && <Loader />}
        <div className="mt-8 text-center">
          <p className="text-base">
            {t("already_have_account")}{" "}
            <Link to="/login" className="font-bold text-primary underline">
              {t("login")}
            </Link>
          </p>
        </div>
      </AuthLayout>

      <Modal
        header={t("verifying_number")}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)} 
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleConfirm();
          }}
        >
          <p className="text-body-text mb-24">
            {t("are_you_sure_that")}
            <strong dir="ltr" className="text-primary-text mx-2">
              {displayPhoneNumber}
            </strong>
            {t("your_correct_number?")}
          </p>
          <div className="flex justify-end gap-4">
            <GeneralButton
              title={t("edit")}
              color="lite"
              onClick={() => setIsModalOpen(false)}
              className="!w-32"
            />
            <GeneralButton
              title={t("continue")}
              type="submit"
              className="!w-60"
            />
          </div>
        </form>
      </Modal>
    </FormProvider>
  );
};

export default SignUp;
